import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Baby, FileText, FileSignature,
  Users, DollarSign, Building2, ClipboardList, BookOpen, TrendingUp, Bell,
  ClipboardCheck, Settings, Inbox, ShieldCheck, LogOut, ShieldAlert, CalendarClock, X,
  Palette, CalendarRange, ChevronDown, ChevronRight, List, HeartPulse, ArrowLeftRight, MessageCircle, UserCircle, UserPlus,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS, allowedRhTabs, canSeeFinancials, isStructureLocked } from '../../utils/permissions';
import { echeancesApi } from '../../utils/api';

// Arborescence du menu : un « Tableau de bord » à plat, puis 6 groupes dépliables.
const TREE = [
  { type: 'link', to: '/', icon: LayoutDashboard, label: 'Tableau de bord', end: true },
  { type: 'link', to: '/mon-espace', icon: UserCircle, label: 'Mon espace' },
  {
    type: 'group', key: 'enfants', icon: Baby, label: 'Enfants', children: [
      { to: '/enfants', icon: List, label: 'Liste des enfants' },
      { to: '/contrats', icon: FileSignature, label: 'Contrats' },
      { to: '/suivi-medical', icon: HeartPulse, label: 'Suivi médical', badge: 'ech' },
      { to: '/transmissions', icon: ClipboardList, label: 'Transmissions' },
      { to: '/planning', base: '/planning', planning: 'reel', icon: CalendarRange, label: 'Planning occupation' },
      { to: '/planning-enfants', icon: Baby, label: 'Planning enfants' },
    ],
  },
  {
    type: 'group', key: 'preinscription', icon: ClipboardCheck, label: 'Préinscription', children: [
      { to: '/preinscriptions', icon: ClipboardCheck, label: 'Pré-inscriptions' },
      { to: '/demandes', icon: Inbox, label: 'Demandes entrantes' },
      { to: '/planning?vue=previsionnel', base: '/planning', planning: 'prev', icon: CalendarRange, label: 'Planning prévisionnel' },
    ],
  },
  {
    type: 'group', key: 'rh', icon: Users, label: 'Ressources Humaines', children: [
      { to: '/rh?tab=employes', base: '/rh', rhTab: 'employes', icon: Users, label: 'Salariés' },
      { to: '/rh?tab=conges', base: '/rh', rhTab: 'conges', icon: CalendarClock, label: 'Congés & absences' },
      { to: '/rh?tab=planning', base: '/rh', rhTab: 'planning', icon: CalendarRange, label: 'Planning équipe' },
      { to: '/rh?tab=releve', base: '/rh', rhTab: 'releve', icon: FileText, label: 'Relevé mensuel' },
      { to: '/rh?tab=recrutement', base: '/rh', rhTab: 'recrutement', icon: UserPlus, label: 'Recrutement' },
      { to: '/rh?tab=refacturation', base: '/rh', rhTab: 'refacturation', icon: ArrowLeftRight, label: 'Refacturation' },
    ],
  },
  { type: 'link', to: '/whatsapp', icon: MessageCircle, label: 'Messagerie WhatsApp' },
  {
    type: 'group', key: 'gestion', icon: DollarSign, label: 'Gestion', children: [
      { to: '/finance', icon: DollarSign, label: 'Finance' },
      { to: '/statistiques', icon: TrendingUp, label: 'Statistiques' },
      { to: '/analytics', icon: TrendingUp, label: 'Analyse & Optim.' },
    ],
  },
  {
    type: 'group', key: 'ressources', icon: BookOpen, label: 'Ressources', children: [
      { to: '/depot', icon: Inbox, label: 'Déposer un document' },
      { to: '/ressources-docs', icon: BookOpen, label: 'Documents de référence' },
      { to: '/signatures', icon: FileSignature, label: 'Signature électronique' },
      { to: '/protocoles', icon: ShieldAlert, label: 'Protocoles' },
      { to: '/documents', icon: BookOpen, label: 'Générateur de documents' },
      { to: '/courriers', icon: FileText, label: 'Modèles & courriers' },
      { to: '/alertes', icon: Bell, label: 'Alertes réglementaires' },
    ],
  },
  {
    type: 'group', key: 'admin', icon: Settings, label: 'Administration', children: [
      { to: '/parametres', icon: Settings, label: 'Paramètres' },
      { to: '/tarifs', icon: DollarSign, label: 'Grille tarifaire' },
      { to: '/marque', icon: Palette, label: 'Identité & Charte' },
      { to: '/utilisateurs', icon: ShieldCheck, label: 'Comptes & accès' },
    ],
  },
];

const basePath = (child) => child.base || child.to.split('?')[0];

const OPEN_KEY = 'georges_menu_groupes';

export default function Sidebar({ open = false, onClose = () => {} }) {
  const { structures, selectedStructure, setSelectedStructure } = useApp();
  const { user, logout, can } = useAuth();
  // Compte de crèche (tablette) : la structure est imposée, pas de sélecteur.
  const structureFigee = isStructureLocked(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const [echCount, setEchCount] = useState(0);

  useEffect(() => {
    if (!can('/echeances')) { setEchCount(0); return; }
    const params = selectedStructure ? { structure_id: selectedStructure } : {};
    echeancesApi.getResume(params)
      .then(r => setEchCount((r.data.urgent || 0) + (r.data.depasse || 0)))
      .catch(() => {});
  }, [location.pathname, selectedStructure, can]);

  // Est-ce que cette sous-entrée est active vu l'URL courante ?
  const isChildActive = (child) => {
    const bp = basePath(child);
    const onPath = location.pathname === bp || (bp !== '/' && location.pathname.startsWith(bp + '/'));
    if (!onPath) return false;
    if (child.planning) {
      const prev = new URLSearchParams(location.search).get('vue') === 'previsionnel';
      return child.planning === 'prev' ? prev : !prev;
    }
    if (child.rhTab) {
      const cur = new URLSearchParams(location.search).get('tab') || 'employes';
      return cur === child.rhTab;
    }
    return true;
  };

  const rhTabs = allowedRhTabs(user?.role);
  const rhFinance = canSeeFinancials(user?.role);
  // Groupes filtrés par permissions (un groupe n'apparaît que s'il a ≥1 entrée visible)
  const visibleTree = useMemo(() => TREE.map(node => {
    if (node.type === 'link') {
      // « Mon espace » n'a de sens que si le compte est rattaché à une fiche RH
      if (node.to === '/mon-espace' && !user?.employe_id) return null;
      return can(node.to) ? node : null;
    }
    const children = node.children.filter(c => {
      if (!can(basePath(c))) return false;
      if (c.rhTab) {
        if (rhTabs && !rhTabs.includes(c.rhTab)) return false;
        if (c.rhTab === 'refacturation' && !rhFinance) return false;
      }
      return true;
    });
    return children.length ? { ...node, children } : null;
  }).filter(Boolean), [can, rhTabs, rhFinance, user]);

  // Quel groupe contient la route active ?
  const activeGroupKey = useMemo(() => {
    const g = visibleTree.find(n => n.type === 'group' && n.children.some(isChildActive));
    return g ? g.key : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTree, location.pathname, location.search]);

  // État déplié : mémorisé en localStorage, + ouverture auto du groupe actif.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(OPEN_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    if (activeGroupKey && !openGroups[activeGroupKey]) {
      setOpenGroups(prev => ({ ...prev, [activeGroupKey]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  };

  const childClass = (active) =>
    `flex items-center gap-2.5 pl-3 pr-3 py-2 rounded-lg text-[13px] transition-colors ${
      active ? 'bg-gold-500 text-navy-900 font-semibold shadow-sm' : 'text-slate-300 hover:bg-navy-600 hover:text-white'
    }`;

  return (
    <aside className={`w-64 bg-sidebar flex flex-col z-40 fixed inset-y-0 left-0 transform transition-transform duration-200 md:static md:translate-x-0 md:h-screen ${open ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg viewBox="0 0 44 28" width="23" height="15" className="text-navy-900" fill="currentColor" aria-hidden="true"><path d="M20 14 L5 5 Q3 4 3 6.5 L3 21.5 Q3 24 5 23 Z"/><path d="M24 14 L39 5 Q41 4 41 6.5 L41 21.5 Q41 24 39 23 Z"/><rect x="18.3" y="9.3" width="7.4" height="9.4" rx="2.2"/></svg>
          </div>
          <div>
            <div className="text-white font-serif font-semibold text-[17px] leading-tight tracking-wide">Hello Georges</div>
            <div className="text-gold-300/80 text-[11px] tracking-wide">SAS Les Cachettes</div>
          </div>
          <button type="button" onClick={onClose} className="ml-auto p-1.5 rounded-lg text-slate-400 hover:bg-navy-600 hover:text-white md:hidden">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Compte de crèche : rappel de la structure (non modifiable) */}
      {structureFigee && selectedStructure && (
        <div className="px-4 py-4 border-b border-slate-700">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-2 px-1">Structure</div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-white"
            style={{ backgroundColor: structures.find(s => s.id === selectedStructure)?.couleur || '#334155' }}>
            <Building2 size={14} className="flex-shrink-0" />
            <span className="truncate text-xs">{structures.find(s => s.id === selectedStructure)?.nom || '—'}</span>
          </div>
        </div>
      )}

      {/* Structure selector (masqué pour un compte de crèche : structure imposée) */}
      <div className={`px-4 py-4 border-b border-slate-700 ${structureFigee ? 'hidden' : ''}`}>
        <div className="text-slate-400 text-xs uppercase tracking-wider mb-2 px-1">Structure</div>
        <div className="flex flex-col gap-1">
          <button
            onClick={() => { setSelectedStructure(null); navigate('/'); onClose(); }}
            className={`text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
              selectedStructure === null ? 'bg-gold-500 text-navy-900 font-semibold' : 'text-slate-300 hover:bg-navy-600'
            }`}
          >
            <span className="flex items-center gap-2"><Building2 size={14} /> Vue générale</span>
          </button>
          {structures.map(s => (
            <button
              key={s.id}
              onClick={() => { setSelectedStructure(s.id); navigate('/'); onClose(); }}
              className={`text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedStructure === s.id ? 'text-white font-medium' : 'text-slate-300 hover:bg-navy-600'
              }`}
              style={selectedStructure === s.id ? { backgroundColor: s.couleur } : {}}
            >
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.couleur }} />
                <span className="truncate text-xs">{s.nom}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation en arborescence */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="text-slate-400 text-xs uppercase tracking-wider mb-2 px-2">Navigation</div>
        <div className="flex flex-col gap-0.5">
          {visibleTree.map(node => {
            if (node.type === 'link') {
              return (
                <NavLink
                  key={node.to}
                  to={node.to}
                  end={node.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-gold-500 text-navy-900 font-semibold shadow-sm' : 'text-slate-300 hover:bg-navy-600 hover:text-white'
                    }`
                  }
                >
                  <node.icon size={17} />
                  <span className="flex-1">{node.label}</span>
                </NavLink>
              );
            }

            const expanded = !!openGroups[node.key];
            const groupActive = node.key === activeGroupKey;
            return (
              <div key={node.key}>
                <button
                  onClick={() => toggleGroup(node.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    groupActive && !expanded ? 'text-white bg-navy-600/50' : 'text-slate-300 hover:bg-navy-600 hover:text-white'
                  }`}
                >
                  <node.icon size={17} />
                  <span className="flex-1 text-left font-medium">{node.label}</span>
                  {expanded ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
                </button>
                {expanded && (
                  <div className="mt-0.5 mb-1 ml-3 pl-3 border-l border-navy-600 flex flex-col gap-0.5">
                    {node.children.map(child => {
                      const active = isChildActive(child);
                      return (
                        <NavLink key={child.to} to={child.to} onClick={onClose} className={() => childClass(active)}>
                          <child.icon size={15} />
                          <span className="flex-1">{child.label}</span>
                          {child.badge === 'ech' && echCount > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center flex-shrink-0">
                              {echCount}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Footer : utilisateur connecté + déconnexion */}
      <div className="px-3 py-3 border-t border-navy-600">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {(user?.nom || user?.identifiant || '?').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{user?.nom || user?.identifiant}</div>
            <div className="text-gold-300/80 text-[11px]">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <button onClick={logout} title="Se déconnecter"
            className="p-2 rounded-lg text-slate-400 hover:bg-navy-600 hover:text-white transition-colors flex-shrink-0">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
